import { Visibility } from "@mui/icons-material";
import { Button, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import AssessmentPreviewModal from "components/AssessmentPreviewModal";
import AssessmentQuestionsPanel from "components/AssessmentQuestionsPanel";
import ConfirmModal from "components/ConfirmDialog/ConfirmModal";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import FormField from "layouts/applications/wizard/components/FormField";
import generateAssessmentQuestions from "layouts/pages/assessments/actions/generateAssessmentQuestions";
import generateAssessmentRubric from "layouts/pages/assessments/actions/generateAssessmentRubric";
import AssessmentsQuestionsExcelLocal from "layouts/pages/assessments/components/AssessmentsQuestions/AssessmentsQuestionsExcelLocal";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";
import { v4 as uuidv4 } from "uuid";

const useStyle = makeStyles({
  titleBar: { marginBottom: "15px" },

  titleBar_title: { my: 1, ml: 2 },

  section_images_wrapper: {
    overflowX: "auto",
    paddingTop: 10,
    whiteSpace: "nowrap",
  },

  positioner: {
    position: "absolute",
  },

  section_image_container: {
    display: "inline-block",
    position: "relative",
    cursor: "pointer",
  },
  section_image_container2: {
    display: "inline-block",
    position: "relative",
    margin: "0px 8px",
    cursor: "pointer",
  },

  training_section_image_container: {
    display: "inline-block",
    position: "relative",
    margin: "0px 8px",
    cursor: "pointer",
  },

  section_image: {
    height: "100px",
  },

  training_section_image_delete: {
    position: "relative",
    left: "1000%",
    top: "-15px",
    backgroundColor: "white",
    padding: 5,
  },

  maps_section_image_delete: {
    position: "absolute",
    left: "150px",
    top: "-15px",
    backgroundColor: "white",
    padding: 5,
  },

  section_image_delete: {
    position: "absolute",
    right: "-9px",
    top: "-9px",
    backgroundColor: "#fff",
    width: "15px",
    height: "15px",
  },

  otherSection: {
    marginBottom: "24px",
  },
  section_images_wapper: {
    overflowX: "hidden",
    whiteSpace: "nowrap",
    padding: "24px 0px 24px 0px",
    maxWidth: "100%",
  },
  error: {
    fontSize: "0.75rem",
  },
  card: {
    height: 350,
  },
  stack: {
    padding: 10,
  },
  editor: {
    height: 100,
  },
  box: {
    overflow: "visible",
  },
  addButton: {
    fontSize: 14,
    marginRight: 15,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100px",
    border: "1px dashed #C9C9C9",
    padding: "20px 20px",
    cursor: "pointer",
    borderRadius: "4px",
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const QuestionsStep = ({
  clearErrors,
  setValue,
  control,
  getValues,
  errors,
  trigger,
  watch,
  jobSlug,
  excelFiles,
  onSubmitFiles,
}) => {
  const styles = useStyle();
  const { company, currentLoggedUser } = useAppContextController();
  const queryClient = useQueryClient();
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [numQuestionsToGenerate, setNumQuestionsToGenerate] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(null);

  useWatch({
    control,
  });

  const recalculateRecommendedNumberOfQuestions = (questions) => {
    if (!(questions ?? []).length) return 30;
    return Math.ceil((28 * (questions ?? []).length) / 60);
  };

  const { mutateAsync: generateAssessmentQuestionMutation, isLoading: isLoadingQuestion } =
    useMutation(generateAssessmentQuestions, {
      onSuccess: async (res, vars) => {
        const questionsWithIds = res.questions?.map((q) => ({
          ...q,
          _id: q._id || uuidv4(),
        }));
        enqueueSnackbar("Questions generated successfully.", { variant: "success" });
        const newQuestions = vars.existingQuestions?.length
          ? [...vars.existingQuestions, ...questionsWithIds]
          : questionsWithIds;
        setValue("questions", newQuestions, { shouldDirty: true });
        setValue("scoringRubric", res.rubric, { shouldDirty: true });
        setValue("timeLimit", recalculateRecommendedNumberOfQuestions(newQuestions) * 60, {
          shouldDirty: true,
        });
      },
      onError: (err) =>
        enqueueSnackbar(`An error occured: ${err?.response?.data?.message ?? err}`, {
          variant: "error",
        }),
    });

  const { mutateAsync: generateAssessmentRubricMutation, isLoading: isLoadingRubric } = useMutation(
    generateAssessmentRubric,
    {
      onSuccess: async (res, vars) => {
        enqueueSnackbar("Scoring rubric generated successfully.", { variant: "success" });
        setValue("scoringRubric", res.rubric, { shouldDirty: true });
      },
      onError: (err) =>
        enqueueSnackbar(`An error occured: ${err?.response?.data?.message ?? err}`, {
          variant: "error",
        }),
    }
  );

  const addQuestion = (question) => {
    const newQuestions = [...(watch("questions") ?? []), question];
    setValue("questions", newQuestions, { shouldDirty: true });
    setValue("timeLimit", recalculateRecommendedNumberOfQuestions(newQuestions) * 60, {
      shouldDirty: true,
    });
  };

  const updateQuestion = (id, question) => {
    setValue(
      "questions",
      watch("questions").map((oldQuestion) => {
        if (oldQuestion._id === id) {
          return question;
        }
        return oldQuestion;
      }),
      { shouldDirty: true }
    );
  };

  const removeQuestion = (index) => {
    const newQuestions = watch("questions").filter((oldQuestion, oldIndex) => {
      return oldIndex !== index;
    });
    setValue("questions", newQuestions, { shouldDirty: true });
    setValue("timeLimit", recalculateRecommendedNumberOfQuestions(newQuestions) * 60, {
      shouldDirty: true,
    });
  };

  const generateNewQuestions = async (numQuestions, shouldAppend = false) => {
    try {
      enqueueSnackbar(
        `Generating questions... This takes approximately ${numQuestions * 6} seconds.`,
        {
          variant: "warning",
          autoHideDuration: 8000,
        }
      );
      const { questions: generatedQuestions, rubric: scoringRubric } =
        await generateAssessmentQuestionMutation({
          description: watch("description"),
          skills: watch("skills"),
          type: watch("type"),
          numQuestions,
          existingQuestions: shouldAppend ? watch("questions") : [],
        });
    } catch (error) {
      console.error("Failed to generate questions:", error);
    }
  };

  const generateNewRubric = async () => {
    try {
      const { questions: generatedQuestions, rubric: scoringRubric } =
        await generateAssessmentRubricMutation({
          description: watch("description"),
          skills: watch("skills"),
          type: watch("type"),
          existingQuestions: watch("questions"),
        });
    } catch (error) {
      console.error("Failed to generate questions:", error);
    }
  };

  const handleGenerateQuestions = async (numQuestions) => {
    const div = document.createElement("div");
    div.innerHTML = watch("description") ?? "";
    const plainDescription = div.innerText;
    if (!plainDescription.length) {
      enqueueSnackbar(`A description is required`, { variant: "error" });
      return;
    }

    const hasExistingQuestions = watch("questions")?.length > 0;
    if (!hasExistingQuestions) {
      generateNewQuestions(numQuestions);
      return;
    }

    setNumQuestionsToGenerate(numQuestions);
    setShowRegenerate(true);
  };

  const handleConfirmRegenerate = (shouldAppend) => {
    setShowRegenerate(false);
    generateNewQuestions(numQuestionsToGenerate, shouldAppend);
  };

  const handleOpenPreviewModal = () => {
    setOpenPreviewModal(true);
  };

  const handleClosePreviewModal = () => {
    setOpenPreviewModal(false);
  };

  return (
    <>
      <Grid container item xs={12} rowSpacing={4}>
        <Grid item xs={12}>
          {watch("type") === "Excel" ? (
            <AssessmentsQuestionsExcelLocal
              onSubmitFiles={onSubmitFiles}
              localFiles={excelFiles}
              isLocal
            />
          ) : (
            <MDBox>
              <AssessmentQuestionsPanel
                questions={watch("questions") ?? []}
                addQuestion={addQuestion}
                removeQuestion={removeQuestion}
                updateQuestion={updateQuestion}
                questionIndex={questionIndex}
                setQuestionIndex={setQuestionIndex}
                control={control}
                watch={watch}
                errors={errors}
                setValue={setValue}
                title="Assessment Questions"
                onPressGenerate={handleGenerateQuestions}
                isLoadingGenerateQuestion={isLoadingQuestion}
                skills={watch("skills")}
                assessmentType={watch("type")}
              />
            </MDBox>
          )}
        </Grid>

        <Grid item xs={3}>
          <Controller
            name="timeLimit"
            control={control}
            render={({ field }) => (
              <FormField
                type="number"
                label="Time limit (minutes)"
                name="timeLimit"
                {...field}
                value={field.value == null ? "" : Math.round((field.value ?? 0) / 60)}
                onChange={(test) => {
                  if (test?.target?.value == null || test?.target?.value === "")
                    setValue("timeLimit", "", { shouldDirty: true });
                  setValue("timeLimit", test.target.value * 60, { shouldDirty: true });
                }}
              />
            )}
          />
          <MDTypography
            className={styles.error}
          >{`Recommended: ${recalculateRecommendedNumberOfQuestions(watch("questions"))} minutes${
            (watch("questions") ?? []).length
              ? ` (${(watch("questions") ?? []).length} questions)`
              : ""
          }`}</MDTypography>
          {errors?.timeLimit && (
            <MDTypography color="error" className={styles.error}>
              {errors?.timeLimit.message}
            </MDTypography>
          )}
        </Grid>
        {watch("type") !== "Excel" && (
          <Grid container item xs={9} justifyContent="flex-end">
            <Button
              variant="text"
              type="button"
              onClick={() => handleOpenPreviewModal()}
              startIcon={<Visibility />}
              color="info"
              sx={{ color: "#3498db", fontSize: 14, padding: "0" }}
            >
              Preview Questions
            </Button>
          </Grid>
        )}
      </Grid>
      <AssessmentPreviewModal
        open={openPreviewModal}
        onClose={handleClosePreviewModal}
        questions={watch("questions")}
        name={watch("name")}
        description={watch("description")}
        assessment={watch()}
      />
      {showRegenerate && (
        <ConfirmModal
          title="Generate Questions"
          description="Do you want to append to or replace existing questions?"
          isOpen={showRegenerate}
          negativeBtn={{
            label: "Cancel",
            fn: () => {
              setShowRegenerate(false);
            },
            isShown: true,
          }}
          negativeAltBtn={{
            label: "Replace",
            fn: () => handleConfirmRegenerate(false),
            isShown: true,
          }}
          positiveBtn={{
            label: "Append",
            fn: () => handleConfirmRegenerate(true),
            isShown: true,
          }}
        />
      )}
    </>
  );
};

export default QuestionsStep;
